import React, { useState, useEffect, useRef } from "react";
import {
  useLocation,
  useParams,
} from "react-router-dom";
import { WorkspaceLink, useWsNavigate } from "../../hooks/useWsNavigate";
import { useAuth } from "../../contexts/AuthContext";
import { workspaceService } from "../../services/workspaceService";
import {
  Home,
  BookOpen,
  BarChart2,
  Sparkles,
  ClipboardList,
  LogOut,
  Building2,
  ChevronsUpDown,
  SlidersHorizontal,
  FlaskConical,
  MessageSquare,
  Bell,
  LineChart,
  Settings,
  ArrowLeft,
  CreditCard,
  Users,
  Puzzle,
  Key,
  Check,
  Plus,
  Rocket,
  Search,
} from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";
import { useFeature } from "../../hooks/useFeature";
import { useOnboarding } from "../../contexts/OnboardingContext";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "../ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const initial = (str) => (str || "W").charAt(0).toUpperCase();

// Above this many workspaces the switcher grows a search box. Below it, the
// list is short enough that a filter is just noise.
const WORKSPACE_SEARCH_THRESHOLD = 8;

// ── Workspace switcher (sidebar-07 TeamSwitcher pattern) ───────────────────
export const WorkspaceSwitcher = () => {
  const { isMobile } = useSidebar();
  const { workspaces, currentWorkspace, switchWorkspace, createWorkspace, user } =
    useAuth();
  const location = useLocation();
  const { clientUuid } = useParams();
  const canCreateWorkspace = !!user?.can_create_workspace;

  // Switching workspace changes the URL UUID. Do a FULL page reload (not an SPA
  // transition) so the new UUID lands in the address bar and every page reloads
  // fresh against the new workspace. Keep the user on the same logical page;
  // detail pages that don't exist in the new workspace show their not-found.
  const goToWorkspace = (workspace) => {
    switchWorkspace(workspace); // optimistic localStorage set before the reload
    const rest = clientUuid
      ? location.pathname.replace(`/${clientUuid}`, "")
      : "";
    window.location.assign(`/${workspace.uuid}${rest || "/dashboard"}`);
  };

  const [workspaceQuery, setWorkspaceQuery] = useState("");
  const searchRef = useRef(null);
  const searchable = workspaces.length > WORKSPACE_SEARCH_THRESHOLD;
  const visibleWorkspaces = React.useMemo(() => {
    const q = workspaceQuery.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter((w) => (w.name || "").toLowerCase().includes(q));
  }, [workspaces, workspaceQuery]);

  const [showCreate, setShowCreate] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [creating, setCreating] = useState(false);
  const [workspaceError, setWorkspaceError] = useState(null);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [selectedBillingAccountId, setSelectedBillingAccountId] = useState("");

  // Load billing accounts when the create modal opens so we know whether to
  // show the account picker (multi-account users must choose).
  useEffect(() => {
    if (!showCreate) return;
    let active = true;
    workspaceService
      .getAvailableBillingAccounts()
      .then((accounts) => {
        if (!active) return;
        const list = accounts || [];
        setBillingAccounts(list);
        setSelectedBillingAccountId(list.length === 1 ? String(list[0].id) : "");
      })
      .catch(() => active && setBillingAccounts([]));
    return () => {
      active = false;
    };
  }, [showCreate]);

  const needsAccountChoice = billingAccounts.length > 1;

  const closeCreate = () => {
    setShowCreate(false);
    setNewWorkspaceName("");
    setSelectedBillingAccountId("");
    setWorkspaceError(null);
  };

  const handleCreateWorkspace = async (e) => {
    e?.preventDefault?.();
    const name = newWorkspaceName.trim();
    if (!name) return;
    if (needsAccountChoice && !selectedBillingAccountId) {
      setWorkspaceError("Select a billing account for this workspace");
      return;
    }
    try {
      setCreating(true);
      setWorkspaceError(null);
      const created = await createWorkspace({
        name,
        billingAccountId: selectedBillingAccountId || undefined,
      });
      await switchWorkspace(created);
      closeCreate();
      window.location.assign(`/${created.uuid}/dashboard`);
    } catch (err) {
      setWorkspaceError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to create workspace",
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu onOpenChange={(open) => !open && setWorkspaceQuery("")}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground text-sm font-semibold">
                  {initial(currentWorkspace?.name)}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {currentWorkspace?.name || "Workspace"}
                  </span>
                  <span className="truncate text-xs capitalize">
                    {currentWorkspace?.role || "Member"}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 rounded-lg"
              align="start"
              side={isMobile ? "bottom" : "right"}
              sideOffset={4}
              onOpenAutoFocus={(e) => {
                if (!searchable) return;
                // Land the caret in the search box instead of the first item.
                e.preventDefault();
                requestAnimationFrame(() => searchRef.current?.focus());
              }}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Workspaces
              </DropdownMenuLabel>
              {searchable && (
                <div className="px-1 pb-1">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                      ref={searchRef}
                      type="text"
                      value={workspaceQuery}
                      onChange={(e) => setWorkspaceQuery(e.target.value)}
                      // Radix's menu typeahead swallows printable keys; keep them
                      // in the input but let Escape/Tab/arrows reach the menu.
                      onKeyDown={(e) => {
                        if (
                          e.key !== "Escape" &&
                          e.key !== "Tab" &&
                          !e.key.startsWith("Arrow")
                        ) {
                          e.stopPropagation();
                        }
                      }}
                      placeholder="Search workspaces"
                      className="h-8 w-full rounded-md border border-border bg-background pl-7 pr-2 text-sm outline-none placeholder:text-muted-foreground focus:border-ring"
                    />
                  </div>
                </div>
              )}
              <div className="max-h-[19rem] overflow-y-auto overflow-x-hidden">
                {visibleWorkspaces.map((workspace) => {
                  const isActive = currentWorkspace?.id === workspace.id;
                  return (
                    <DropdownMenuItem
                      key={workspace.id}
                      onClick={() => goToWorkspace(workspace)}
                      className="gap-2 p-2"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border bg-sidebar-primary text-sidebar-primary-foreground text-[11px] font-semibold">
                        {initial(workspace.name)}
                      </div>
                      <span className="flex-1 truncate">{workspace.name}</span>
                      {isActive && <Check className="size-4 shrink-0" />}
                    </DropdownMenuItem>
                  );
                })}
                {visibleWorkspaces.length === 0 && (
                  <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                    No workspaces match
                  </div>
                )}
              </div>
              {canCreateWorkspace && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="gap-2 p-2"
                    onClick={() => setShowCreate(true)}
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                      <Plus className="size-4" />
                    </div>
                    <div className="font-medium text-muted-foreground">
                      New workspace
                    </div>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      {showCreate && canCreateWorkspace && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onMouseDown={closeCreate}
        >
          <form
            onSubmit={handleCreateWorkspace}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-xl border border-border bg-background p-4 shadow-lg"
          >
            <h2 className="text-base font-semibold text-foreground">
              Create workspace
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Workspaces keep agents, runs, and billing separate.
            </p>
            <input
              type="text"
              autoFocus
              placeholder="Workspace name"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              className="mt-4 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {needsAccountChoice && (
              <select
                value={selectedBillingAccountId}
                onChange={(e) => setSelectedBillingAccountId(e.target.value)}
                className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Billing account…</option>
                {billingAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.primary_workspace || `Account #${a.id}`}
                    {a.plan_code ? ` · ${a.plan_code}` : ""}
                  </option>
                ))}
              </select>
            )}
            {workspaceError && (
              <p className="mt-2 text-xs text-destructive">{workspaceError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCreate}
                className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  !newWorkspaceName.trim() ||
                  creating ||
                  (needsAccountChoice && !selectedBillingAccountId)
                }
                className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

// ── User menu (sidebar-07 NavUser pattern) ─────────────────────────────────
const NavUser = () => {
  const { isMobile } = useSidebar();
  const navigate = useWsNavigate();
  const { user, removeToken } = useAuth();
  const { isAdmin } = usePermissions();

  const handleLogout = () => {
    removeToken();
    navigate("/login");
  };

  const settingsItems = [
    { key: "workspace", name: "Workspace", icon: Building2 },
    { key: "integrations", name: "Integrations", icon: Puzzle },
    ...(isAdmin
      ? [
          { key: "teams", name: "Teams", icon: Users },
          { key: "api_keys", name: "API Keys", icon: Key },
          { key: "billing", name: "Billing", icon: CreditCard },
        ]
      : []),
  ];

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground text-sm font-semibold">
                {initial(user?.name || user?.email)}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {user?.name || "User"}
                </span>
                <span className="truncate text-xs">{user?.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <div className="flex size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground text-sm font-semibold">
                  {initial(user?.name || user?.email)}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {user?.name || "User"}
                  </span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {settingsItems.map((item) => (
                <DropdownMenuItem
                  key={item.key}
                  onClick={() => navigate(`/settings/${item.key}`)}
                >
                  <item.icon />
                  {item.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

// ── Setup guide entry (onboarding sidebar item) ────────────────────────────
export const SetupGuideEntry = () => {
  const { canWrite, isComplete, completed, total, reopen } = useOnboarding();
  const navigate = useWsNavigate();

  if (!canWrite || isComplete) return null;

  const handleClick = () => {
    reopen();
    navigate("/onboarding");
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton onClick={handleClick}>
          <Rocket />
          <span className="flex-1">Setup guide</span>
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary">
            {completed}/{total}
          </span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

const AppSidebar = () => {
  const location = useLocation();
  const { clientUuid } = useParams();
  // Path with the workspace UUID segment stripped, so active-state checks can
  // compare against plain hrefs ("/dashboard") regardless of the prefix.
  const appPath =
    clientUuid && location.pathname.startsWith(`/${clientUuid}`)
      ? location.pathname.slice(clientUuid.length + 1) || "/"
      : location.pathname;
  const isSettingsPage = appPath.startsWith("/settings");
  const { isAdmin } = usePermissions();

  // Feature flags (unconditional — rules of hooks)
  const featureLiveSimulations = useFeature("live_simulations.simulator");
  const featureObservabilityConversations = useFeature(
    "observability.conversations",
  );
  const featureObservabilityDiscovery = useFeature("observability.discovery");
  const featurePromptOptimizer = useFeature("prompt_optimizer.optimization_runs");
  const featureAnalytics = useFeature("analytics.embedded_dashboards");
  const featureMetrics = useFeature("metrics.custom_metrics");
  const featureBatch = useFeature("batch_runs.batch_management");

  const primaryNav = [
    { name: "Dashboard", href: "/dashboard", icon: Home },
    { name: "Test Agents", href: "/ai-testing-agent", icon: FlaskConical },
    {
      name: "Metrics",
      href: "/evaluation/metrics",
      icon: BarChart2,
      featureEnabled: featureMetrics,
    },
    {
      name: "Analytics",
      href: "/analytics",
      icon: LineChart,
      featureEnabled: featureAnalytics,
    },
    {
      name: "Co-Pilot",
      href: "/copilot",
      icon: Sparkles,
      featureEnabled: featureObservabilityDiscovery,
    },
    {
      name: "Prompt Optimizer",
      href: "/prompt-optimizer",
      icon: SlidersHorizontal,
      featureEnabled: featurePromptOptimizer,
    },
  ].filter((item) => item.featureEnabled !== false);

  const simulationSection = [
    {
      name: "Live Simulations",
      href: "/live-simulations/scenario",
      icon: BookOpen,
      featureEnabled: featureLiveSimulations,
    },
    {
      name: "Batch",
      href: "/batch-run",
      icon: ClipboardList,
      featureEnabled: featureBatch,
    },
  ].filter((item) => item.featureEnabled !== false);

  const observabilitySection = [
    {
      name: "Conversations",
      href: "/observability/conversations",
      icon: MessageSquare,
      featureEnabled: featureObservabilityConversations,
    },
    {
      name: "Notifications",
      href: "/observability/notifications",
      icon: Bell,
      featureEnabled: featureObservabilityConversations,
    },
  ].filter((item) => item.featureEnabled !== false);

  const settingsNavigation = [
    { key: "workspace", name: "Workspace", icon: Building2 },
    { key: "integrations", name: "Integrations", icon: Puzzle },
    ...(isAdmin ? [{ key: "teams", name: "Teams", icon: Users }] : []),
    ...(isAdmin
      ? [
          { key: "api_keys", name: "API Keys", icon: Key },
          { key: "billing", name: "Billing", icon: CreditCard },
        ]
      : []),
  ];

  const isActiveHref = (href, name) => {
    if (name === "Test Agents") {
      return (
        appPath.includes("/ai-testing-agent") ||
        appPath.includes("/edit-agent")
      );
    }
    return appPath === href || appPath.startsWith(href + "/");
  };

  // Settings sections are path-based now: /settings/<section>.
  const activeSettingsTab = appPath.split("/")[2] || "workspace";

  const renderItems = (items) => (
    <SidebarMenu>
      {items.map((item) => (
        <SidebarMenuItem key={item.name}>
          <SidebarMenuButton
            asChild
            isActive={isActiveHref(item.href, item.name)}
            tooltip={item.name}
          >
            <WorkspaceLink to={item.href}>
              <item.icon />
              <span>{item.name}</span>
            </WorkspaceLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <WorkspaceSwitcher />
      </SidebarHeader>

      <SidebarContent>
        {isSettingsPage ? (
          <SidebarGroup>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Back to Dashboard">
                  <WorkspaceLink to="/dashboard">
                    <ArrowLeft />
                    <span>Back to Dashboard</span>
                  </WorkspaceLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarMenu>
              {settingsNavigation.map((item) => {
                const to = `/settings/${item.key}`;
                return (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton
                      asChild
                      isActive={activeSettingsTab === item.key}
                      tooltip={item.name}
                    >
                      <WorkspaceLink to={to}>
                        <item.icon />
                        <span>{item.name}</span>
                      </WorkspaceLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ) : (
          <>
            <SidebarGroup>
              <SidebarGroupLabel>Platform</SidebarGroupLabel>
              {renderItems(primaryNav)}
            </SidebarGroup>
            {simulationSection.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel>Evaluation</SidebarGroupLabel>
                {renderItems(simulationSection)}
              </SidebarGroup>
            )}
            {observabilitySection.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel>Observability</SidebarGroupLabel>
                {renderItems(observabilitySection)}
              </SidebarGroup>
            )}
            <SidebarGroup>
              <SetupGuideEntry />
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      <SidebarFooter>
        {!isSettingsPage && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Settings">
                <WorkspaceLink to="/settings">
                  <Settings />
                  <span>Settings</span>
                </WorkspaceLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        <NavUser />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
